import type { SxProps } from '@mui/material'
import Box from '@mui/material/Box'
import MuiButton from '@mui/material/Button'
import type { TouchRippleActions } from '@mui/material/ButtonBase/TouchRipple'
import { styled } from '@mui/material/styles'
import React, { FC, useMemo } from 'react'

import { Loading, Text } from '../index'
import { LuneTheme, LuneThemeNew } from '../theme'

const { palette } = LuneThemeNew

export type ButtonVariant = 'primary' | 'secondary'
export type ButtonStyle = 'fill' | 'outline' | 'floating'
export type ButtonSize = 'lg' | 'sm'

const baseStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    lineHeight: '24px',
}

type Spec = {
    bg: { default: string; hover: string; active: string; disabled: string }
    color: { default: string; hover: string; active: string; disabled: string }
    border?: { default: string; hover: string; active: string; disabled: string }
}

const buttonSpecs: Record<ButtonVariant, Record<ButtonStyle, Spec>> = {
    primary: {
        fill: {
            bg: {
                default: `ButtonPrimaryFilledBg`,
                hover: `ButtonPrimaryFilledBgHover`,
                active: `ButtonPrimaryFilledBgActive`,
                disabled: `ButtonPrimaryFilledBgDisabled`,
            },
            color: {
                default: `TextInverse`,
                hover: `TextInverse`,
                active: `TextInverse`,
                disabled: `TextDisabled`,
            },
        },
        outline: {
            bg: {
                default: `transparent`,
                hover: `ButtonPrimaryOutlinedBgHover`,
                active: `ButtonPrimaryOutlinedBgHover`,
                disabled: `transparent`,
            },
            color: {
                default: `TextPrimary`,
                hover: `TextPrimary`,
                active: `TextPrimary`,
                disabled: `TextDisabled`,
            },
            border: {
                default: `ButtonPrimaryOutlinedBorder`,
                hover: `ButtonPrimaryOutlinedBorderHover`,
                active: `ButtonPrimaryOutlinedBorderActive`,
                disabled: `ButtonPrimaryOutlinedBorderDisabled`,
            },
        },
        floating: {
            bg: {
                default: `transparent`,
                hover: `ButtonFloatingBgHover`,
                active: `ButtonFloatingBgActive`,
                disabled: `transparent`,
            },
            color: {
                default: `TextSecondary`,
                hover: `TextSecondary`,
                active: `TextSecondary`,
                disabled: `TextDisabled`,
            },
        },
    },
    secondary: {
        fill: {
            bg: {
                default: `ButtonSecondaryFilledBg`,
                hover: `ButtonSecondaryFilledBgHover`,
                active: `ButtonSecondaryFilledBgActive`,
                disabled: `ButtonSecondaryFilledBgDisabled`,
            },
            color: {
                default: `TextSecondary`,
                hover: `TextSecondary`,
                active: `TextInverse`,
                disabled: `TextDisabled`,
            },
        },
        outline: {
            bg: {
                default: `transparent`,
                hover: `ButtonSecondaryOutlinedBgHover`,
                active: `ButtonSecondaryOutlinedBgHover`,
                disabled: `transparent`,
            },
            color: {
                default: `TextSecondary`,
                hover: `TextSecondary`,
                active: `TextSecondary`,
                disabled: `TextDisabled`,
            },
            border: {
                default: `ButtonSecondaryOutlinedBorder`,
                hover: `ButtonSecondaryOutlinedBorderHover`,
                active: `ButtonSecondaryOutlinedBorderActive`,
                disabled: `ButtonSecondaryOutlinedBorderDisabled`,
            },
        },
        floating: {
            bg: {
                default: `transparent`,
                hover: `ButtonFloatingBgHover`,
                active: `ButtonFloatingBgActive`,
                disabled: `transparent`,
            },
            color: {
                default: `TextSecondary`,
                hover: `TextSecondary`,
                active: `TextSecondary`,
                disabled: `TextDisabled`,
            },
        },
    },
}

// eslint-disable-next-line complexity
const getButtonStyles = (variant: ButtonVariant, style: ButtonStyle, disabled: boolean) => {
    const spec = buttonSpecs[variant][style]
    const pickFromPalette = (key: string) =>
        key ? palette[key as keyof typeof palette] : undefined
    const bg = spec.bg
    const border = spec.border
    const color = spec.color

    return {
        ...baseStyles,
        backgroundColor: pickFromPalette(disabled ? bg.disabled : bg.default),
        color: pickFromPalette(disabled ? color.disabled : color.default),
        ...(border && {
            border: `1px solid ${pickFromPalette(disabled ? border.disabled : border.default)}`,
        }),

        '&:hover': {
            backgroundColor: pickFromPalette(disabled ? bg.disabled : bg.hover),
            color: pickFromPalette(disabled ? color.disabled : color.hover),
            ...(border && {
                borderColor: pickFromPalette(disabled ? border.disabled : border.hover),
            }),
        },

        '&:active': {
            backgroundColor: pickFromPalette(disabled ? bg.disabled : bg.active),
            color: pickFromPalette(disabled ? color.disabled : color.active),
            ...(border && {
                borderColor: pickFromPalette(disabled ? border.disabled : border.active),
            }),
        },

        ...(style === 'outline' && !border && { border: 'none' }),
        ...(style !== 'outline' && { border: 'none' }),
    }
}

export interface ButtonNewProps {
    children?: React.ReactNode
    variant?: ButtonVariant
    style?: ButtonStyle
    size?: ButtonSize
    label?: string
    leadingIcon?: React.ReactNode
    trailingIcon?: React.ReactNode
    onClick?: (event: React.MouseEvent<HTMLElement>) => void
    type?: 'button' | 'submit' | 'reset'
    disabled?: boolean
    hideLabel?: boolean
    sx?: SxProps
    href?: string
    target?: string
    rel?: string
    download?: React.AnchorHTMLAttributes<HTMLAnchorElement>['download']
    touchRippleRef?: React.Ref<TouchRippleActions>
    className?: string
    loading?: boolean
}

const ButtonNew: FC<ButtonNewProps> = ({
    children,
    variant = 'primary',
    style = 'fill',
    size = 'lg',
    label,
    leadingIcon,
    trailingIcon,
    onClick,
    type = 'button',
    disabled = false,
    hideLabel = false,
    sx,
    href,
    target,
    className,
    loading,
    ...rest
}) => {
    // Memoizing was needed to prevent the dropdown panel from floating away from the button on the Dropdown component
    const StyledButton = useMemo(
        () =>
            styled(MuiButton)(
                LuneTheme.unstable_sx({
                    height: size === 'lg' ? '48px' : '40px',
                    minWidth: hideLabel ? 'auto' : size === 'lg' ? '90px' : '90px',
                    padding: size === 'lg' ? '0 16px' : '0 12px',
                    borderRadius: size === 'lg' ? '12px' : '8px',
                    boxShadow: 'none',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        boxShadow: 'none',
                    },
                    '&:active': {
                        boxShadow: 'none',
                    },
                    '&:focus': {
                        boxShadow: 'none',
                    },
                    svg: {
                        width: size === 'lg' ? '24px' : '20px',
                        height: size === 'lg' ? '24px' : '20px',
                        color: 'inherit',
                    },
                    '.MuiTypography-root': {
                        lineHeight: size === 'lg' ? '48px' : '40px',
                    },
                }),
            ),
        [size, hideLabel],
    )

    const buttonStyles = getButtonStyles(variant, style, !!disabled)
    const finalSx = { ...(buttonStyles as SxProps), ...(sx || {}) }

    const buttonContent = (
        <>
            {loading && (
                <Box
                    sx={{
                        maxHeight: '100%',
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        margin: 'auto',
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Loading
                        light={style === 'fill'}
                        sx={{
                            height: size === 'lg' ? '32px' : '20px',
                            width: size === 'lg' ? '32px' : '32px',
                        }}
                    />
                </Box>
            )}
            <Box
                sx={{
                    opacity: loading ? 0 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                }}
            >
                {leadingIcon}
                {!hideLabel && <Text variant={'button'}>{label || children}</Text>}
                {trailingIcon}
            </Box>
        </>
    )

    return (
        <StyledButton
            type={type}
            onClick={onClick}
            disabled={disabled || loading}
            sx={finalSx}
            {...(href ? { href } : {})}
            {...(target ? { target } : {})}
            {...rest}
            className={className}
        >
            {buttonContent}
        </StyledButton>
    )
}

export default ButtonNew
